import {
  LegalLayout,
  Heading,
  Text,
  Link,
  RouterLink,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
} from '../components/LegalLayout';

const subprocessors = [
  {
    name: 'Microsoft Azure',
    purpose: 'Cloud infrastructure, hosting, database services, and blob storage',
    dataProcessed: 'Application data, user account data, uploaded content',
    location: 'United States',
    privacyUrl: 'https://privacy.microsoft.com/en-us/privacystatement',
  },
  {
    name: 'Anthropic',
    purpose: 'AI-powered text analysis for pick extraction and content processing',
    dataProcessed: 'Publicly available sports betting content for analytical processing',
    location: 'United States',
    privacyUrl: 'https://www.anthropic.com/privacy',
  },
  {
    name: 'OpenAI',
    purpose: 'AI-powered text and image analysis for pick extraction and OCR processing',
    dataProcessed: 'Publicly available sports betting content and images for analytical processing',
    location: 'United States',
    privacyUrl: 'https://openai.com/policies/privacy-policy',
  },
  {
    name: 'Datadog',
    purpose: 'Application performance monitoring, logging, and infrastructure observability',
    dataProcessed: 'Application logs, performance metrics, error traces (no direct user PII)',
    location: 'United States',
    privacyUrl: 'https://www.datadoghq.com/legal/privacy/',
  },
  {
    name: 'Cursor / Anysphere',
    purpose: 'AI-assisted development tooling used in building and maintaining the Services',
    dataProcessed: 'Source code and development artifacts (no user PII)',
    location: 'United States',
    privacyUrl: 'https://www.cursor.com/privacy',
  },
  {
    name: 'Featurebase',
    purpose: 'User feedback collection, feature request tracking, and bug reporting',
    dataProcessed: 'User-submitted feedback, feature requests, and email addresses',
    location: 'United States',
    privacyUrl: 'https://www.featurebase.app/privacy',
  },
  {
    name: 'Mailgun',
    purpose: 'Transactional email delivery for account notifications, password resets, and system communications',
    dataProcessed: 'Email addresses, email content, and delivery metadata',
    location: 'United States',
    privacyUrl: 'https://www.mailgun.com/legal/privacy-policy/',
  },
  {
    name: 'Stripe',
    purpose: 'Payment processing for subscriptions and billing',
    dataProcessed: 'Payment card details, billing information, and transaction metadata',
    location: 'United States',
    privacyUrl: 'https://stripe.com/privacy',
  },
];

export default function Subprocessors() {
  return (
    <LegalLayout title="Subprocessors" lastUpdated="June 7, 2026">
      <Text>
        IvoryShoulder, LLC, doing business as WagerWatchdog
        (&ldquo;WagerWatchdog,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;our&rdquo;), uses the following third-party service providers
        (&ldquo;Subprocessors&rdquo;) to assist in providing our Services.
        Each Subprocessor is contractually obligated to handle data in
        accordance with applicable data protection laws and our{' '}
        <Link as={RouterLink} to="/privacy">
          Privacy Policy
        </Link>
        .
      </Text>

      <Heading as="h2">Current Subprocessors</Heading>
      <Text>
        The following table lists our current Subprocessors, the services they
        provide, the types of data they may process, and their locations.
      </Text>

      <Box overflowX="auto" mb={8}>
        <Table
          variant="simple"
          size="sm"
          sx={{
            'th': {
              color: 'gray.400',
              borderColor: 'whiteAlpha.200',
              fontSize: 'xs',
              textTransform: 'uppercase',
              letterSpacing: 'wider',
              fontWeight: 'semibold',
              py: 3,
            },
            'td': {
              borderColor: 'whiteAlpha.100',
              py: 3,
              fontSize: 'sm',
            },
            'tr': {
              _hover: { bg: 'whiteAlpha.50' },
            },
          }}
        >
          <Thead>
            <Tr>
              <Th>Subprocessor</Th>
              <Th>Purpose</Th>
              <Th>Data Processed</Th>
              <Th>Location</Th>
            </Tr>
          </Thead>
          <Tbody>
            {subprocessors.map((sp) => (
              <Tr key={sp.name}>
                <Td fontWeight="medium" color="white" whiteSpace="nowrap">
                  <Link href={sp.privacyUrl} isExternal>
                    {sp.name}
                  </Link>
                </Td>
                <Td>{sp.purpose}</Td>
                <Td>{sp.dataProcessed}</Td>
                <Td whiteSpace="nowrap">{sp.location}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Heading as="h2">Changes to Subprocessors</Heading>
      <Text>
        We may update the list of Subprocessors from time to time as we add,
        remove, or replace service providers. This page will always reflect the
        current list of Subprocessors. We encourage you to check this page
        periodically for updates. The &ldquo;Last updated&rdquo; date at the
        top of this page indicates when this list was last modified.
      </Text>

      <Heading as="h2">Data Protection</Heading>
      <Text>
        We carefully select Subprocessors based on their security practices,
        data protection capabilities, and compliance posture. Each Subprocessor
        is required to:
      </Text>
      <ul>
        <li>
          Process data only in accordance with our documented instructions.
        </li>
        <li>
          Implement appropriate technical and organizational security measures.
        </li>
        <li>
          Notify us promptly of any data security incidents.
        </li>
        <li>
          Delete or return data upon termination of the service relationship.
        </li>
        <li>
          Comply with applicable data protection regulations, including GDPR
          where applicable.
        </li>
      </ul>

      <Heading as="h2">Questions</Heading>
      <Text>
        If you have questions about our Subprocessors or data processing
        practices, contact us at{' '}
        <Link href="mailto:privacy@wagerwatchdog.com">
          privacy@wagerwatchdog.com
        </Link>
        .
      </Text>
    </LegalLayout>
  );
}
